import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Save, Trash2, Plus, ExternalLink, User, Link2, Briefcase, Calendar, Palette } from 'lucide-react';
import {
  fetchManagedUserProfile,
  updateManagedUserProfile,
  deleteManagedProfileItem,
  type ManagedUserProfileData,
  type ManagedSocialLink,
  type ManagedCustomLink,
  type ManagedCollaboration,
} from '../../services/superadmin';

interface ManageUserProfileProps {
  userId: number;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

type TabType = 'profile' | 'theme' | 'social' | 'custom' | 'collaborations' | 'events';

const ManageUserProfile: React.FC<ManageUserProfileProps> = ({
  userId,
  userName,
  userEmail,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [data, setData] = useState<ManagedUserProfileData | null>(null);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    tagline: '',
    location: '',
    phone: '',
    bio: '',
    profile_url: '',
  });
  const [themeForm, setThemeForm] = useState({
    theme_style: 'theme2',
    bg_color: '#FDF6EC',
    card_color: '#E6CBA8',
    text_color: 'white',
    icon_shape: 'circle',
  });
  const [socialLinks, setSocialLinks] = useState<ManagedSocialLink[]>([]);
  const [customLinks, setCustomLinks] = useState<ManagedCustomLink[]>([]);
  const [collaborations, setCollaborations] = useState<ManagedCollaboration[]>([]);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    setLoading(true);
    try {
      const response = await fetchManagedUserProfile(userId);
      if (response.data.success) {
        setData(response.data.data);
        
        // Initialize forms
        if (response.data.data.profile) {
          setProfileForm({
            name: response.data.data.profile.name || '',
            tagline: response.data.data.profile.tagline || '',
            location: response.data.data.profile.location || '',
            phone: response.data.data.profile.phone || '',
            bio: response.data.data.profile.bio || '',
            profile_url: response.data.data.profile.profile_url || '',
          });
          setThemeForm({
            theme_style: response.data.data.profile.theme_style || 'theme2',
            bg_color: response.data.data.profile.bg_color || '#FDF6EC',
            card_color: response.data.data.profile.card_color || '#E6CBA8',
            text_color: response.data.data.profile.text_color || 'white',
            icon_shape: response.data.data.profile.icon_shape || 'circle',
          });
        }
        
        setSocialLinks(response.data.data.social_links || []);
        setCustomLinks(response.data.data.custom_links || []);
        setCollaborations(response.data.data.collaborations || []);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateManagedUserProfile(userId, {
        profile: profileForm,
      });
      toast.success('Profile updated successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      await updateManagedUserProfile(userId, {
        profile: themeForm,
      });
      toast.success('Theme updated successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update theme');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSocialLinks = async () => {
    setSaving(true);
    try {
      await updateManagedUserProfile(userId, {
        social_links: socialLinks.map((link) => ({
          platform: link.platform,
          url: link.url,
        })),
      });
      toast.success('Social links updated successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update social links');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomLinks = async () => {
    setSaving(true);
    try {
      await updateManagedUserProfile(userId, {
        custom_links: customLinks.map((link) => ({
          label: link.label,
          url: link.url,
          icon: link.icon || undefined,
        })),
      });
      toast.success('Custom links updated successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update custom links');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCollaborations = async () => {
    setSaving(true);
    try {
      await updateManagedUserProfile(userId, {
        collaborations: collaborations.map((collab) => ({
          name: collab.name,
          logo: collab.logo || undefined,
          description: collab.description || undefined,
          url: collab.url || undefined,
        })),
      });
      toast.success('Collaborations updated successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update collaborations');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (type: 'social_link' | 'custom_link' | 'collaboration' | 'upi_payment' | 'event', itemId: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      await deleteManagedProfileItem(userId, type, itemId);
      toast.success('Item deleted successfully');
      loadUserData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete item');
    }
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { id: 0, profile_id: 0, platform: '', url: '', display_order: socialLinks.length }]);
  };

  const addCustomLink = () => {
    setCustomLinks([...customLinks, { id: 0, profile_id: 0, label: '', url: '', icon: null, display_order: customLinks.length }]);
  };

  const addCollaboration = () => {
    setCollaborations([...collaborations, { id: 0, profile_id: 0, name: '', logo: null, description: null, url: null, display_order: collaborations.length }]);
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'profile', label: 'Profile', icon: <User className="h-4 w-4" /> },
    { id: 'theme', label: 'Theme', icon: <Palette className="h-4 w-4" /> },
    { id: 'social', label: 'Social Links', icon: <Link2 className="h-4 w-4" /> },
    { id: 'custom', label: 'Custom Links', icon: <Link2 className="h-4 w-4" /> },
    { id: 'collaborations', label: 'Collaborations', icon: <Briefcase className="h-4 w-4" /> },
    { id: 'events', label: 'Events', icon: <Calendar className="h-4 w-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Profile</h2>
            <p className="text-sm text-gray-500">
              {userName} ({userEmail})
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data?.profile?.profile_url && (
              <a
                href={`/${data.profile.profile_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View Profile
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span className="ml-2">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Profile URL</label>
                    <input
                      type="text"
                      value={profileForm.profile_url}
                      onChange={(e) => setProfileForm({ ...profileForm, profile_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                    <input
                      type="text"
                      value={profileForm.tagline}
                      onChange={(e) => setProfileForm({ ...profileForm, tagline: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={profileForm.location}
                      onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="text"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={profileForm.bio}
                      onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </div>
              )}

              {/* Theme Tab */}
              {activeTab === 'theme' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Theme Style</label>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'theme1', label: 'Split Layout' },
                        { value: 'theme2', label: 'Simple Card' },
                        { value: 'theme3', label: 'Professional Card' },
                      ].map((theme) => (
                        <button
                          key={theme.value}
                          type="button"
                          onClick={() => setThemeForm({ ...themeForm, theme_style: theme.value })}
                          className={`p-4 rounded-lg border-2 transition-all text-center ${
                            themeForm.theme_style === theme.value
                              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-sm font-medium text-gray-700">{theme.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color Theme</label>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { bg: '#FDF6EC', card: '#E6CBA8', label: 'Champagne Elegance' },
                        { bg: '#F5E6FA', card: '#D4B8E8', label: 'Soft Royal Lavender' },
                        { bg: '#E8F5F0', card: '#B8D4C8', label: 'Mint Luxury' },
                        { bg: '#FFF5F0', card: '#E8C8B8', label: 'Ivory & Rose Gold' },
                      ].map((color, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setThemeForm({ ...themeForm, bg_color: color.bg, card_color: color.card })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            themeForm.bg_color === color.bg && themeForm.card_color === color.card
                              ? 'border-blue-500 ring-2 ring-blue-200'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex gap-1 mb-2">
                            <div className="h-6 flex-1 rounded-l" style={{ backgroundColor: color.bg }} />
                            <div className="h-6 flex-1 rounded-r" style={{ backgroundColor: color.card }} />
                          </div>
                          <span className="text-xs font-medium text-gray-700">{color.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                      <select
                        value={themeForm.text_color}
                        onChange={(e) => setThemeForm({ ...themeForm, text_color: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="white">White</option>
                        <option value="black">Black</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Icon Shape</label>
                      <select
                        value={themeForm.icon_shape}
                        onChange={(e) => setThemeForm({ ...themeForm, icon_shape: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="circle">Circle</option>
                        <option value="rounded">Rounded</option>
                        <option value="square">Square</option>
                      </select>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                    <div
                      className="rounded-lg p-6 transition-colors"
                      style={{ backgroundColor: themeForm.bg_color }}
                    >
                      <div
                        className="rounded-lg p-4 transition-colors"
                        style={{ backgroundColor: themeForm.card_color }}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-10 h-10 flex items-center justify-center bg-white ${
                              themeForm.icon_shape === 'circle' ? 'rounded-full' :
                              themeForm.icon_shape === 'rounded' ? 'rounded-lg' : 'rounded-none'
                            }`}
                          >
                            <span className="text-lg">👤</span>
                          </div>
                          <div>
                            <p
                              className="font-medium"
                              style={{ color: themeForm.text_color === 'white' ? '#ffffff' : '#000000' }}
                            >
                              {profileForm.name || 'User Name'}
                            </p>
                            <p
                              className="text-sm opacity-80"
                              style={{ color: themeForm.text_color === 'white' ? '#ffffff' : '#000000' }}
                            >
                              {profileForm.tagline || 'Tagline'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSaveTheme}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Theme'}
                    </button>
                  </div>
                </div>
              )}

              {/* Social Links Tab */}
              {activeTab === 'social' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Social Links</h3>
                    <button
                      onClick={addSocialLink}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Link
                    </button>
                  </div>
                  {socialLinks.map((link, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Platform"
                          value={link.platform}
                          onChange={(e) => {
                            const newLinks = [...socialLinks];
                            newLinks[index].platform = e.target.value;
                            setSocialLinks(newLinks);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="URL"
                          value={link.url}
                          onChange={(e) => {
                            const newLinks = [...socialLinks];
                            newLinks[index].url = e.target.value;
                            setSocialLinks(newLinks);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {link.id > 0 && (
                        <button
                          onClick={() => handleDeleteItem('social_link', link.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSaveSocialLinks}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Social Links'}
                    </button>
                  </div>
                </div>
              )}

              {/* Custom Links Tab */}
              {activeTab === 'custom' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Custom Links</h3>
                    <button
                      onClick={addCustomLink}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Link
                    </button>
                  </div>
                  {customLinks.map((link, index) => (
                    <div key={index} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Label"
                          value={link.label}
                          onChange={(e) => {
                            const newLinks = [...customLinks];
                            newLinks[index].label = e.target.value;
                            setCustomLinks(newLinks);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="URL"
                          value={link.url}
                          onChange={(e) => {
                            const newLinks = [...customLinks];
                            newLinks[index].url = e.target.value;
                            setCustomLinks(newLinks);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {link.id > 0 && (
                        <button
                          onClick={() => handleDeleteItem('custom_link', link.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSaveCustomLinks}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Custom Links'}
                    </button>
                  </div>
                </div>
              )}

              {/* Collaborations Tab */}
              {activeTab === 'collaborations' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Collaborations</h3>
                    <button
                      onClick={addCollaboration}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Collaboration
                    </button>
                  </div>
                  {collaborations.map((collab, index) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Name"
                          value={collab.name}
                          onChange={(e) => {
                            const newCollabs = [...collaborations];
                            newCollabs[index].name = e.target.value;
                            setCollaborations(newCollabs);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="URL"
                          value={collab.url || ''}
                          onChange={(e) => {
                            const newCollabs = [...collaborations];
                            newCollabs[index].url = e.target.value;
                            setCollaborations(newCollabs);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <textarea
                        placeholder="Description"
                        value={collab.description || ''}
                        onChange={(e) => {
                          const newCollabs = [...collaborations];
                          newCollabs[index].description = e.target.value;
                          setCollaborations(newCollabs);
                        }}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {collab.id > 0 && (
                        <button
                          onClick={() => handleDeleteItem('collaboration', collab.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={handleSaveCollaborations}
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Collaborations'}
                    </button>
                  </div>
                </div>
              )}

              {/* Events Tab */}
              {activeTab === 'events' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Events</h3>
                  {data?.events && data.events.length > 0 ? (
                    <div className="space-y-3">
                      {data.events.map((event) => (
                        <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: event.color }}
                            />
                            <div>
                              <p className="font-medium">{event.title}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(event.start_date).toLocaleDateString()} - {event.type}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteItem('event', event.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No events found</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageUserProfile;
